/**
 * Comprehensive Parameter Validation Tests
 * 
 * This utility provides automated tests to verify parameter count consistency
 * across all configuration files and detect discrepancies that could lead to
 * system reliability issues.
 * 
 * Tests include:
 * - Parameter count consistency across files
 * - Naming convention compliance
 * - Configuration completeness
 * - Runtime validation alerts
 */

import { EXPECTED_PARAMETER_COUNT, validateParameterCount } from './parameterCountValidator';
import { PARAMETERS_MD_PARAMETERS, auditParameterConfiguration } from './parameterAudit';

/**
 * Comprehensive parameter system validation
 * @param {Array} tableData - Processed table data from useHealthSnapshotData
 * @param {Object} configData - Configuration data from scoring configs
 * @returns {Object} Validation results with detailed breakdown
 */
export const runComprehensiveParameterValidation = (tableData, configData) => {
  const validationResults = {
    timestamp: new Date().toISOString(),
    overallStatus: 'UNKNOWN',
    tests: {},
    summary: {},
    recommendations: []
  };

  // Test 1: Parameter Count Consistency
  validationResults.tests.parameterCountConsistency = testParameterCountConsistency(tableData);
  
  // Test 2: Configuration Completeness
  validationResults.tests.configurationCompleteness = testConfigurationCompleteness(configData);
  
  // Test 3: Naming Convention Compliance
  validationResults.tests.namingConventionCompliance = testNamingConventionCompliance(tableData);
  
  // Test 4: Duplicate Detection
  validationResults.tests.duplicateDetection = testDuplicateDetection();
  
  // Test 5: Runtime Parameter Processing
  validationResults.tests.runtimeProcessing = testRuntimeParameterProcessing(tableData);

  // Calculate overall status
  const testResults = Object.values(validationResults.tests);
  const passedTests = testResults.filter(test => test.status === 'PASS').length;
  const failedTests = testResults.filter(test => test.status === 'FAIL').length;
  const warningTests = testResults.filter(test => test.status === 'WARNING').length;

  if (failedTests > 0) {
    validationResults.overallStatus = 'FAIL';
  } else if (warningTests > 0) {
    validationResults.overallStatus = 'WARNING';
  } else {
    validationResults.overallStatus = 'PASS';
  }

  validationResults.summary = {
    totalTests: testResults.length,
    passed: passedTests,
    failed: failedTests,
    warnings: warningTests,
    successRate: `${((passedTests / testResults.length) * 100).toFixed(1)}%`
  };

  // Generate recommendations
  validationResults.recommendations = generateRecommendations(validationResults.tests);

  return validationResults;
};

/**
 * Test parameter count consistency across all sources
 */
const testParameterCountConsistency = (tableData) => {
  const test = {
    name: 'Parameter Count Consistency',
    status: 'UNKNOWN',
    details: {},
    issues: []
  };

  try {
    // Get counts from different sources
    const parameterMdCount = PARAMETERS_MD_PARAMETERS.length;
    const expectedCount = EXPECTED_PARAMETER_COUNT;
    const processedCount = tableData ? tableData.reduce((total, group) => 
      total + (group.parameters ? group.parameters.length : 0), 0) : 0;

    test.details = {
      parameterMdActual: parameterMdCount,
      expectedCountSetting: expectedCount,
      processedCount: processedCount,
      discrepancies: []
    };

    // Check for discrepancies
    if (parameterMdCount !== expectedCount) {
      test.issues.push(`EXPECTED_PARAMETER_COUNT (${expectedCount}) doesn't match actual parameters.md count (${parameterMdCount})`);
      test.details.discrepancies.push('expected_vs_actual');
    }

    if (processedCount > expectedCount + 100) { // Allow for derived parameters
      test.issues.push(`Processed count (${processedCount}) significantly exceeds expected (${expectedCount})`);
      test.details.discrepancies.push('processed_vs_expected');
    }

    test.status = test.issues.length === 0 ? 'PASS' : 'FAIL';
  } catch (error) {
    test.status = 'FAIL';
    test.issues.push(`Test execution error: ${error.message}`);
  }

  return test;
};

/**
 * Test configuration completeness
 */
const testConfigurationCompleteness = (configData) => {
  const test = {
    name: 'Configuration Completeness',
    status: 'UNKNOWN',
    details: {},
    issues: []
  };

  try {
    if (!configData || !configData.parameters) {
      test.status = 'FAIL';
      test.issues.push('No configuration data provided');
      return test;
    }

    const configuredParams = Object.keys(configData.parameters);
    const auditResult = auditParameterConfiguration(configuredParams);

    test.details = {
      totalExpected: auditResult.totalExpected,
      totalConfigured: auditResult.totalConfigured,
      missingCount: auditResult.missingCount,
      extraCount: auditResult.extraCount,
      completionRate: `${((auditResult.totalConfigured / auditResult.totalExpected) * 100).toFixed(1)}%`
    };

    if (auditResult.missingCount > 0) {
      test.issues.push(`${auditResult.missingCount} parameters missing from configuration`);
    }

    if (auditResult.extraCount > 50) { // Allow some extra for derived parameters
      test.issues.push(`${auditResult.extraCount} extra parameters in configuration (may indicate duplicates)`);
    }

    test.status = test.issues.length === 0 ? 'PASS' : (auditResult.missingCount > 0 ? 'FAIL' : 'WARNING');
  } catch (error) {
    test.status = 'FAIL';
    test.issues.push(`Test execution error: ${error.message}`);
  }

  return test;
};

/**
 * Test naming convention compliance
 */
const testNamingConventionCompliance = (tableData) => {
  const test = {
    name: 'Naming Convention Compliance',
    status: 'UNKNOWN',
    details: {},
    issues: []
  };

  try {
    if (!tableData) {
      test.status = 'WARNING';
      test.issues.push('No table data provided for naming validation');
      return test;
    }

    const allParameters = [];
    tableData.forEach(group => {
      if (group.parameters) {
        group.parameters.forEach(param => {
          allParameters.push(param.key || param.parameterKey);
        });
      }
    });

    const validPrefixes = ['vitals_', 'labs_', 'biometrics_', 'pe_', 'imaging_', 'lifestyle_'];
    const invalidNames = allParameters.filter(paramKey => 
      paramKey && !validPrefixes.some(prefix => paramKey.startsWith(prefix))
    );

    test.details = {
      totalParameters: allParameters.length,
      validParameters: allParameters.length - invalidNames.length,
      invalidParameters: invalidNames.length,
      complianceRate: `${(((allParameters.length - invalidNames.length) / allParameters.length) * 100).toFixed(1)}%`,
      invalidNames: invalidNames.slice(0, 10) // Show first 10 for debugging
    };

    if (invalidNames.length > 0) {
      test.issues.push(`${invalidNames.length} parameters don't follow naming conventions`);
    }

    test.status = invalidNames.length === 0 ? 'PASS' : 'WARNING';
  } catch (error) {
    test.status = 'FAIL';
    test.issues.push(`Test execution error: ${error.message}`);
  }

  return test;
};

/**
 * Test for duplicate parameters
 */
const testDuplicateDetection = () => {
  const test = {
    name: 'Duplicate Detection',
    status: 'UNKNOWN',
    details: {},
    issues: []
  };

  try {
    const paramSet = new Set();
    const duplicates = [];

    PARAMETERS_MD_PARAMETERS.forEach(param => {
      if (paramSet.has(param)) {
        duplicates.push(param);
      } else {
        paramSet.add(param);
      }
    });

    test.details = {
      totalParameters: PARAMETERS_MD_PARAMETERS.length,
      uniqueParameters: paramSet.size,
      duplicateCount: duplicates.length,
      duplicates: duplicates
    };

    if (duplicates.length > 0) {
      test.issues.push(`${duplicates.length} duplicate parameters found in PARAMETERS_MD_PARAMETERS`);
    }

    test.status = duplicates.length === 0 ? 'PASS' : 'FAIL';
  } catch (error) {
    test.status = 'FAIL';
    test.issues.push(`Test execution error: ${error.message}`);
  }

  return test;
};

/**
 * Test runtime parameter processing
 */
const testRuntimeParameterProcessing = (tableData) => {
  const test = {
    name: 'Runtime Parameter Processing',
    status: 'UNKNOWN',
    details: {},
    issues: []
  };

  try {
    if (!tableData) {
      test.status = 'WARNING';
      test.issues.push('No runtime data available for processing validation');
      return test;
    }

    const validation = validateParameterCount(tableData);
    
    test.details = {
      expectedCount: validation.expectedCount,
      actualCount: validation.actualCount,
      discrepancy: validation.discrepancy,
      isValid: validation.isValid,
      categoryBreakdown: validation.categoryBreakdown
    };

    if (!validation.isValid) {
      test.issues.push(`Parameter count mismatch: expected ${validation.expectedCount}, got ${validation.actualCount}`);
    }

    test.status = validation.isValid ? 'PASS' : 'FAIL';
  } catch (error) {
    test.status = 'FAIL';
    test.issues.push(`Test execution error: ${error.message}`);
  }

  return test;
};

/**
 * Generate recommendations based on test results
 */
const generateRecommendations = (tests) => {
  const recommendations = [];

  if (tests.parameterCountConsistency?.status === 'FAIL') {
    recommendations.push('Update EXPECTED_PARAMETER_COUNT to match actual parameters.md count');
    recommendations.push('Investigate source of extra parameters in processing');
  }

  if (tests.configurationCompleteness?.status === 'FAIL') {
    recommendations.push('Add missing parameters to configuration files');
    recommendations.push('Review extra parameters for potential duplicates');
  }

  if (tests.namingConventionCompliance?.status === 'WARNING') {
    recommendations.push('Review parameters with non-standard naming conventions');
    recommendations.push('Update parameter keys to follow established prefixes');
  }

  if (tests.duplicateDetection?.status === 'FAIL') {
    recommendations.push('Remove duplicate entries from PARAMETERS_MD_PARAMETERS array');
    recommendations.push('Update parameters.md to remove duplicate entries');
  }

  if (tests.runtimeProcessing?.status === 'FAIL') {
    recommendations.push('Investigate runtime parameter processing discrepancies');
    recommendations.push('Verify synthetic parameter generation logic');
  }

  return recommendations;
};

/**
 * Runtime validation alert system
 */
export const alertOnParameterDiscrepancies = (validationResults) => {
  if (validationResults.overallStatus === 'FAIL') {
    console.error('🚨 CRITICAL PARAMETER VALIDATION FAILURES DETECTED:');
    validationResults.recommendations.forEach(rec => console.error(`   - ${rec}`));
    
    // In development, throw an error to catch issues early
    if (process.env.NODE_ENV === 'development') {
      console.error('Parameter validation failed in development mode. Check console for details.');
    }
  } else if (validationResults.overallStatus === 'WARNING') {
    console.warn('⚠️ Parameter validation warnings detected:');
    validationResults.recommendations.forEach(rec => console.warn(`   - ${rec}`));
  } else {
    console.log('✅ All parameter validation tests passed');
  }
};
